import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Send, Eye, CheckCircle, Clock, Mail, Plus, Upload, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { leadsAPI } from '@/utils/api';
import TermSheetEditor from '@/components/term-sheets/TermSheetEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TermSheets = () => {
  const location = useLocation();
  const [termSheets, setTermSheets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTS, setSelectedTS] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Form data for creating term sheet
  const [formData, setFormData] = useState({
    lead_id: '',
    lender_name: '',
    program_name: '',
    loan_type: 'Fix and Flip',
    borrower_name: '',
    borrower_email: '',
    borrower_phone: '',
    loan_amount: '',
    property_address: '',
    interest_rate: '',
    term_months: '',
    ltv: '',
    rep_name: '',
    rep_email: '',
    rep_phone: '',
    notes: ''
  });

  // Email form data
  const [emailData, setEmailData] = useState({
    to_email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchTermSheets();
    fetchLeads();
  }, []);

  const fetchTermSheets = async () => {
    try {
      const leadId = location.state?.leadId;
      const url = leadId ? `${API}/term-sheets?lead_id=${leadId}` : `${API}/term-sheets`;
      const response = await axios.get(url, { withCredentials: true });
      setTermSheets(response.data);
      const openId = new URLSearchParams(location.search).get('open');
      if (openId) {
        const found = response.data.find((t) => t.term_sheet_id === openId);
        if (found) { setSelectedTS(found); setShowDetailDialog(true); }
      }
    } catch (error) {
      toast.error('Failed to load term sheets');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to load leads');
    }
  };

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l.lead_id === leadId);
    if (lead) {
      setFormData(prev => ({
        ...prev,
        lead_id: leadId,
        borrower_name: `${lead.first_name} ${lead.last_name}`,
        borrower_email: lead.email,
        borrower_phone: lead.phone || '',
        property_address: lead.property_address || ''
      }));
    }
  };

  const handleCreateTermSheet = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        loan_amount: parseFloat(formData.loan_amount) || 0,
        term_months: parseInt(formData.term_months) || 12,
        ltv: parseFloat(formData.ltv) || 0
      };
      
      await axios.post(`${API}/term-sheets/manual`, submitData, { withCredentials: true });
      toast.success('Term sheet created successfully!');
      setShowCreateDialog(false);
      resetForm();
      fetchTermSheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create term sheet');
    }
  };

  const resetForm = () => {
    setFormData({
      lead_id: '',
      lender_name: '',
      program_name: '',
      loan_type: 'Fix and Flip',
      borrower_name: '',
      borrower_email: '',
      borrower_phone: '',
      loan_amount: '',
      property_address: '',
      interest_rate: '',
      term_months: '',
      ltv: '',
      rep_name: '',
      rep_email: '',
      rep_phone: '',
      notes: ''
    });
  };

  const handleSendTermSheet = async (termSheetId) => {
    try {
      await axios.post(`${API}/term-sheets/${termSheetId}/send`, {}, { withCredentials: true });
      toast.success('Term sheet sent to borrower!');
      fetchTermSheets();
    } catch (error) {
      toast.error('Failed to send term sheet');
    }
  };

  const handleOpenEmailDialog = (ts) => {
    setSelectedTS(ts);
    setEmailData({
      to_email: ts.terms.borrower_email || '',
      subject: `Term Sheet - ${ts.terms.program_name} - ${ts.borrower_name}`,
      message: `Dear ${ts.borrower_name},\n\nPlease find attached the term sheet for your loan application.\n\nLoan Details:\n- Lender: ${ts.terms.lender_name}\n- Program: ${ts.terms.program_name}\n- Loan Amount: $${Number(ts.loan_amount).toLocaleString()}\n- Interest Rate: ${ts.interest_rate}\n- Term: ${ts.term_months} months\n\nPlease review and sign at your earliest convenience.\n\nBest regards,\nAxe Capital Lending`
    });
    setShowEmailDialog(true);
  };

  const handleSendCustomEmail = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      const response = await axios.post(
        `${API}/term-sheets/${selectedTS.term_sheet_id}/email`,
        emailData,
        { withCredentials: true }
      );
      
      if (response.data.email_sent) {
        toast.success('Email sent and logged to lead notes!');
      } else {
        toast.success('Email logged to lead notes (email service not configured)');
      }
      
      setShowEmailDialog(false);
      fetchTermSheets();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleViewDetails = (ts) => {
    setSelectedTS(ts);
    setShowDetailDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-700',
      'sent': 'bg-blue-100 text-blue-700',
      'signed': 'bg-green-100 text-green-700',
      'expired': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const copySignLink = (termSheetId) => {
    const link = `${window.location.origin}/sign/${termSheetId}`;
    navigator.clipboard.writeText(link);
    toast.success('Signing link copied to clipboard!');
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.lead_id === leadId);
    return lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown Lead';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="term-sheets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-primary">Term Sheets</h1>
          <p className="text-muted-foreground mt-2">Manage and track all loan term sheets</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-term-sheet-button">
              <Plus className="h-4 w-4" />
              Add Term Sheet
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Term Sheet</DialogTitle>
              <DialogDescription>
                Create a term sheet and connect it to a lead
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTermSheet} className="space-y-6">
              {/* Lead Selection */}
              <div className="space-y-2">
                <Label>Select Lead *</Label>
                <Select 
                  value={formData.lead_id} 
                  onValueChange={handleLeadSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lead to connect..." />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map((lead) => (
                      <SelectItem key={lead.lead_id} value={lead.lead_id}>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {lead.first_name} {lead.last_name} - {lead.email}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Borrower Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground">BORROWER INFORMATION</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Borrower Name *</Label>
                    <Input
                      value={formData.borrower_name}
                      onChange={(e) => setFormData({...formData, borrower_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Borrower Email *</Label>
                    <Input
                      type="email"
                      value={formData.borrower_email}
                      onChange={(e) => setFormData({...formData, borrower_email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Borrower Phone *</Label>
                    <Input
                      value={formData.borrower_phone}
                      onChange={(e) => setFormData({...formData, borrower_phone: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Lender Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground">LENDER INFORMATION</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lender Name *</Label>
                    <Input
                      value={formData.lender_name}
                      onChange={(e) => setFormData({...formData, lender_name: e.target.value})}
                      placeholder="e.g., ABC Lending"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Program Name *</Label>
                    <Input
                      value={formData.program_name}
                      onChange={(e) => setFormData({...formData, program_name: e.target.value})}
                      placeholder="e.g., Fix and Flip Bridge"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Loan Type *</Label>
                    <Select 
                      value={formData.loan_type} 
                      onValueChange={(v) => setFormData({...formData, loan_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Fix and Flip">Fix and Flip</SelectItem>
                        <SelectItem value="DSCR">DSCR</SelectItem>
                        <SelectItem value="Commercial">Commercial</SelectItem>
                        <SelectItem value="Bridge">Bridge</SelectItem>
                        <SelectItem value="Construction">Construction</SelectItem>
                        <SelectItem value="Refinance">Refinance</SelectItem>
                        <SelectItem value="Purchase">Purchase</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Rep Name</Label>
                    <Input
                      value={formData.rep_name}
                      onChange={(e) => setFormData({...formData, rep_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rep Email</Label>
                    <Input
                      type="email"
                      value={formData.rep_email}
                      onChange={(e) => setFormData({...formData, rep_email: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Loan Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground">LOAN DETAILS</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Loan Amount *</Label>
                    <Input
                      type="number"
                      value={formData.loan_amount}
                      onChange={(e) => setFormData({...formData, loan_amount: e.target.value})}
                      placeholder="500000"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Interest Rate *</Label>
                    <Input
                      value={formData.interest_rate}
                      onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                      placeholder="e.g., 10.5%"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Term (Months) *</Label>
                    <Input
                      type="number"
                      value={formData.term_months}
                      onChange={(e) => setFormData({...formData, term_months: e.target.value})}
                      placeholder="12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>LTV (%) *</Label>
                    <Input
                      type="number"
                      value={formData.ltv}
                      onChange={(e) => setFormData({...formData, ltv: e.target.value})}
                      placeholder="75"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Property Address *</Label>
                  <Textarea
                    value={formData.property_address}
                    onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                    placeholder="Enter the property address"
                    rows={2}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes about this term sheet..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => {setShowCreateDialog(false); resetForm();}}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!formData.lead_id}>
                  Create Term Sheet
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {termSheets.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No term sheets yet. Click "Add Term Sheet" to create one.</p>
          </div>
        ) : (
          termSheets.map((ts, index) => (
            <Card key={ts.term_sheet_id} className="hover:shadow-lg transition-shadow" data-testid={`term-sheet-${index}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{ts.borrower_name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {ts.terms.program_name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Lead: {getLeadName(ts.lead_id)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={getStatusColor(ts.status)}>
                      {ts.status === 'signed' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {ts.status === 'sent' && <Clock className="h-3 w-3 mr-1" />}
                      {ts.status.toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className={ts.kind === 'final' ? 'border-emerald-600 text-emerald-700' : 'border-amber-500 text-amber-700'} data-testid={`term-sheet-kind-${index}`}>
                      {ts.kind === 'final' ? 'FINAL — lender confirmed' : 'PRELIMINARY — estimate'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Lender</span>
                    <span className="font-medium">{ts.terms.lender_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Loan Amount</span>
                    <span className="font-medium">${Number(ts.loan_amount).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rate</span>
                    <span className="font-medium text-primary">{ts.interest_rate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Term</span>
                    <span className="font-medium">{ts.term_months} months</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">LTV</span>
                    <span className="font-medium">{ts.ltv}%</span>
                  </div>
                </div>

                {ts.signed_at && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      Signed: {new Date(ts.signed_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1" 
                    onClick={() => handleViewDetails(ts)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleOpenEmailDialog(ts)}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTS && (
            <>
              <DialogHeader>
                <DialogTitle>Term Sheet Details</DialogTitle>
                <DialogDescription asChild>
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <Badge className={getStatusColor(selectedTS.status)} data-testid="ts-detail-status-badge">
                      {selectedTS.status.toUpperCase()}
                    </Badge>
                    <span className="text-xs">Lead: {getLeadName(selectedTS.lead_id)}</span>
                  </div>
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="terms" data-testid="ts-terms-tab">Terms & Approval</TabsTrigger>
                  <TabsTrigger value="actions">Actions</TabsTrigger>
                </TabsList>

                <TabsContent value="terms" className="mt-4">
                  <TermSheetEditor
                    key={`${selectedTS.term_sheet_id}-${selectedTS.updated_at}`}
                    termSheet={selectedTS}
                    onChanged={async (updated) => {
                      await fetchTermSheets();
                      const fresh = updated || (await axios.get(`${API}/term-sheets/${selectedTS.term_sheet_id}`, { withCredentials: true })).data;
                      setSelectedTS(fresh);
                    }}
                  />
                </TabsContent>

                <TabsContent value="details" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Borrower</Label>
                      <p className="font-medium">{selectedTS.borrower_name}</p>
                      <p className="text-sm text-muted-foreground">{selectedTS.terms.borrower_email}</p>
                      <p className="text-sm text-muted-foreground">{selectedTS.terms.borrower_phone}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Lender Representative</Label>
                      <p className="font-medium">{selectedTS.terms.rep_name || 'N/A'}</p>
                      <p className="text-sm text-muted-foreground">{selectedTS.terms.rep_email || ''}</p>
                      <p className="text-sm text-muted-foreground">{selectedTS.terms.rep_phone || ''}</p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">Property Address</Label>
                    <p className="font-medium">{selectedTS.property_address}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Program</Label>
                      <p className="font-medium">{selectedTS.terms.program_name}</p>
                      <p className="text-sm text-muted-foreground">{selectedTS.terms.loan_type}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Lender</Label>
                      <p className="font-medium">{selectedTS.terms.lender_name}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Loan Amount</Label>
                      <p className="font-medium text-lg">${selectedTS.loan_amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Interest Rate</Label>
                      <p className="font-medium text-lg text-primary">{selectedTS.interest_rate}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Term</Label>
                      <p className="font-medium text-lg">{selectedTS.term_months} months</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Loan-to-Value (LTV)</Label>
                      <p className="font-medium">{selectedTS.ltv}%</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Max LTV</Label>
                      <p className="font-medium">{selectedTS.terms.max_ltv}%</p>
                    </div>
                  </div>

                  {selectedTS.terms.notes && (
                    <div>
                      <Label className="text-muted-foreground">Notes</Label>
                      <p className="font-medium">{selectedTS.terms.notes}</p>
                    </div>
                  )}

                  {selectedTS.signed_at && (
                    <div className="pt-4 border-t">
                      <Label className="text-muted-foreground">Signed Date</Label>
                      <p className="font-medium">
                        {new Date(selectedTS.signed_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="actions" className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">Signing Link</Label>
                      <div className="flex gap-2 mt-2">
                        <input 
                          type="text" 
                          value={`${window.location.origin}/sign/${selectedTS.term_sheet_id}`}
                          readOnly
                          className="flex-1 px-3 py-2 text-sm border rounded-md"
                        />
                        <Button onClick={() => copySignLink(selectedTS.term_sheet_id)}>
                          Copy
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          setShowDetailDialog(false);
                          handleOpenEmailDialog(selectedTS);
                        }}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Email Term Sheet
                      </Button>
                      {(selectedTS.status === 'approved' || (selectedTS.status === 'draft' && !selectedTS.fields)) && (
                        <Button 
                          variant="secondary"
                          className="flex-1"
                          data-testid="ts-quick-send-btn"
                          onClick={() => {
                            handleSendTermSheet(selectedTS.term_sheet_id);
                            setShowDetailDialog(false);
                          }}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send to Borrower
                        </Button>
                      )}
                      {selectedTS.status === 'draft' && selectedTS.fields && (
                        <p className="text-xs text-amber-700 flex-1 self-center" data-testid="ts-approve-required-note">Approve in “Terms & Approval” before sending.</p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Term Sheet</DialogTitle>
            <DialogDescription>
              Send term sheet to {selectedTS?.borrower_name}. This will be logged in the lead's notes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendCustomEmail} className="space-y-4">
            <div className="space-y-2">
              <Label>To Email *</Label>
              <Input
                type="email"
                value={emailData.to_email}
                onChange={(e) => setEmailData({...emailData, to_email: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={emailData.message}
                onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                rows={8}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEmailDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={emailLoading}>
                {emailLoading ? 'Sending...' : 'Send & Log'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TermSheets;
